import React from 'react';
import { KPIStats } from '../components/Dashboard/KPIStats';
import { RevenueChart } from '../components/Dashboard/RevenueChart';
import { RecentClients } from '../components/Dashboard/RecentClients';
import { ActivityTimeline } from '../components/Dashboard/ActivityTimeline';
import AiChat from '../components/AiChat/AiChat';
import { LegalDashboard } from '../components/Dashboard/LegalDashboard';
import Agenda from './Agenda';
import { Button } from '../components/ui/button';
import { Calendar, Sparkles } from 'lucide-react';
import { Drawer, IconButton, Tooltip } from '@mui/material';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const { user } = useAuth();

  const departmentName = user?.department_name || "";
  const normalizedDepartment = departmentName.toLowerCase();
  const roleName = (user?.role_name || user?.role || "").toLowerCase();
  const isAdmin = roleName === "admin";
  const isAgency = roleName === "agenzia";

  let baseVariant = "default";

  if (normalizedDepartment.includes("commerciale")) {
    baseVariant = "commerciale";
  } else if (normalizedDepartment.includes("legale")) {
    baseVariant = "legale";
  } else if (normalizedDepartment.includes("amministrazione")) {
    baseVariant = "amministrazione";
  } else if (normalizedDepartment.includes("coordinamento")) {
    baseVariant = "coordinamento";
  } else if (normalizedDepartment.includes("risorse umane") || normalizedDepartment.includes("hr")) {
    baseVariant = "risorse umane";
  } else if (normalizedDepartment.includes("lead")) {
    baseVariant = "lead";
  } else if (normalizedDepartment.includes("marketing")) {
    baseVariant = "marketing";
  }

  const [selectedVariant, setSelectedVariant] = React.useState("appuntamenti");

  const currentVariant = selectedVariant || "default";
  const [aiOpen, setAiOpen] = React.useState(false);

  React.useEffect(() => {
    if (isAgency && selectedVariant !== "appuntamenti") {
      setSelectedVariant("appuntamenti");
    }
  }, [isAgency, selectedVariant]);

  const variantMeta = {
    default: {
      title: "Dashboard",
      subtitle: "Benvenuto nel pannello di gestione. Ecco una panoramica delle attività.",
    },
    commerciale: {
      title: "Dashboard Commerciale",
      subtitle: "Focus su lead, conversioni e performance del team commerciale.",
    },
    marketing: {
      title: "Dashboard Marketing",
      subtitle: "KPI campagne, fonti traffico e performance di acquisizione.",
    },
    legale: {
      title: "Dashboard Legale",
      subtitle: "Monitoraggio delle pratiche legali, scadenze e risultati.",
    },
    amministrazione: {
      title: "Dashboard Amministrazione",
      subtitle: "Controllo di incassi, insoluti e fatturazione.",
    },
    coordinamento: {
      title: "Dashboard Coordinamento",
      subtitle: "Visione trasversale delle attività e dei carichi per dipartimento.",
    },
    "risorse umane": {
      title: "Dashboard Risorse Umane",
      subtitle: "Monitoraggio di persone, recruiting e formazione.",
    },
    appuntamenti: {
      title: "Agenda",
      subtitle: "Gestione appuntamenti su più calendari (pubblici e privati).",
    },
    lead: {
      title: "Dashboard Lead",
      subtitle: "Monitoraggio dei lead, fonti e conversioni.",
    },
  };

  const title = variantMeta[currentVariant]?.title || variantMeta.default.title;
  const subtitle = variantMeta[currentVariant]?.subtitle || variantMeta.default.subtitle;

  const adminTabs = [
    { key: "default", label: "Panoramica" },
    { key: "commerciale", label: "Commerciale" },
    { key: "marketing", label: "Marketing" },
    { key: "lead", label: "Lead" },
    { key: "legale", label: "Legale" },
    { key: "amministrazione", label: "Amministrazione" },
    { key: "coordinamento", label: "Coordinamento" },
    { key: "risorse umane", label: "Risorse Umane" },
    { key: "appuntamenti", label: "Agenda" },
  ];

  const variantLabels = {
    default: "Panoramica",
    commerciale: "Commerciale",
    marketing: "Marketing",
    lead: "Lead",
    legale: "Legale",
    amministrazione: "Amministrazione",
    coordinamento: "Coordinamento",
    "risorse umane": "Risorse Umane",
  };

  const userTabs = React.useMemo(() => {
    if (isAgency) return [{ key: "appuntamenti", label: "Agenda" }];
    const tabs = [{ key: "appuntamenti", label: "Agenda" }];
    const normalizedBase = baseVariant || "default";
    if (normalizedBase !== "appuntamenti") {
      tabs.push({
        key: normalizedBase,
        label: variantLabels[normalizedBase] || "Panoramica",
      });
    }
    const deduped = [];
    const seen = new Set();
    for (const tab of tabs) {
      if (seen.has(tab.key)) continue;
      seen.add(tab.key);
      deduped.push(tab);
    }
    return deduped;
  }, [baseVariant, isAgency]);

  const tabsToRender = isAdmin ? adminTabs : userTabs;

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-1">{subtitle}</p>
        </div>
        {!isAgency && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="hidden sm:flex gap-2">
              <Calendar className="h-4 w-4" />
              <span>Ultimi 30 giorni</span>
            </Button>
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm">
              Scarica Report
            </Button>
            <Tooltip title="Apri assistente AI globale">
              <IconButton
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
                onClick={() => setAiOpen(true)}
                size="large"
                aria-label="Assistente AI globale"
              >
                <Sparkles className="h-5 w-5" />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>

      {tabsToRender.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tabsToRender.map((tab) => (
            <Button
              key={tab.key}
              type="button"
              size="sm"
              variant={currentVariant === tab.key ? "default" : "outline"}
              className={currentVariant === tab.key ? "" : "bg-background text-muted-foreground"}
              onClick={() => setSelectedVariant(tab.key)}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      )}

      {currentVariant === 'legale' ? (
        <LegalDashboard />
      ) : currentVariant === 'appuntamenti' ? (
        <Agenda embedded />
      ) : (
        <>
          <KPIStats variant={currentVariant} />

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">
            <div className="col-span-full lg:col-span-4">
              <RevenueChart variant={currentVariant} />
            </div>
            <div className="col-span-full lg:col-span-3">
              <RecentClients variant={currentVariant} />
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-1">
            <div className="col-span-1">
              <ActivityTimeline variant={currentVariant} />
            </div>
          </div>

          <Drawer
            anchor="right"
            open={aiOpen}
            onClose={() => setAiOpen(false)}
            PaperProps={{
              sx: {
                width: { xs: '100%', sm: 420 },
                p: 2,
                boxSizing: 'border-box'
              }
            }}
          >
            <AiChat
              title="Assistente AI"
              subtitle="Chat globale con contesto di workspace."
              context={{}}
            />
          </Drawer>
        </>
      )}
    </div>
  );
}
